import {
  Box,
  Button,
  Field,
  Flex,
  HStack,
  Input,
  Select,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdAdd, MdDelete } from "react-icons/md";
import { TokenEndpointConfig } from "./TokenEndpointConfig";

export type AuthorizationType =
  | "None"
  | "CustomToken"
  | "BasicAuth"
  | "OAuth"
  | "CustomAuthEndpoint";

interface ServerAuthorizationConfigProps {
  authorizationType: AuthorizationType;
  staticAuthHeaders?: string;
  dynamicAuthHeaders?: string;
  allowAuthUpdate: boolean;
  authInstructionsUrl?: string;
  // CustomAuthEndpoint fields
  autoTokenEndpoint?: string;
  autoTokenMethod?: string;
  autoTokenRequestHeaders?: string;
  autoTokenRequestBody?: string;
  autoTokenRequestPlaceholders?: string;
  autoTokenResponseExample?: string;
  autoTokenResponsePath?: string;
  autoTokenHeaderName?: string;
  autoTokenHeaderFormat?: string;
  autoTokenSecret?: string;
  // Refresh token fields
  refreshTokenPath?: string;
  refreshTokenEndpoint?: string;
  refreshTokenMethod?: string;
  refreshTokenRequestHeaders?: string;
  refreshTokenRequestBody?: string;
  refreshTokenResponsePath?: string;
  onChange: (field: string, value: any) => void;
  disabled?: boolean;
  errors?: {
    autoTokenEndpoint?: string;
    autoTokenResponsePath?: string;
    autoTokenHeaderName?: string;
    autoTokenHeaderFormat?: string;
  };
}

export const ServerAuthorizationConfig = ({
  authorizationType,
  staticAuthHeaders,
  dynamicAuthHeaders,
  allowAuthUpdate,
  authInstructionsUrl,
  autoTokenEndpoint,
  autoTokenMethod,
  autoTokenRequestHeaders,
  autoTokenRequestBody,
  autoTokenRequestPlaceholders,
  autoTokenResponseExample,
  autoTokenResponsePath,
  autoTokenHeaderName,
  autoTokenHeaderFormat,
  autoTokenSecret,
  refreshTokenPath,
  refreshTokenEndpoint,
  refreshTokenMethod,
  refreshTokenRequestHeaders,
  refreshTokenRequestBody,
  refreshTokenResponsePath,
  onChange,
  disabled = false,
  errors,
}: ServerAuthorizationConfigProps) => {
  const { t } = useTranslation();

  // Parse JSON strings into arrays for UI
  const [staticHeadersArray, setStaticHeadersArray] = useState<
    Array<{ name: string; value: string }>
  >([]);
  const [dynamicHeadersArray, setDynamicHeadersArray] = useState<
    Array<{ name: string; value: string }>
  >([]);

  // Parse incoming JSON strings
  useEffect(() => {
    if (staticAuthHeaders) {
      try {
        const parsed = JSON.parse(staticAuthHeaders);
        if (typeof parsed === "object" && !Array.isArray(parsed)) {
          // Convert object to array of key-value pairs
          const arr = Object.entries(parsed).map(([name, value]) => ({
            name,
            value: String(value),
          }));
          setStaticHeadersArray(arr);
        }
      } catch {
        setStaticHeadersArray([]);
      }
    } else {
      setStaticHeadersArray([]);
    }
  }, [staticAuthHeaders]);

  useEffect(() => {
    if (dynamicAuthHeaders) {
      try {
        const parsed = JSON.parse(dynamicAuthHeaders);
        if (typeof parsed === "object" && !Array.isArray(parsed)) {
          // Convert object to array of key-value pairs
          const arr = Object.entries(parsed).map(([name, value]) => ({
            name,
            value: String(value),
          }));
          setDynamicHeadersArray(arr);
        }
      } catch {
        setDynamicHeadersArray([]);
      }
    } else {
      setDynamicHeadersArray([]);
    }
  }, [dynamicAuthHeaders]);

  // Convert arrays back to JSON objects when they change
  const handleStaticHeadersChange = (
    headers: Array<{ name: string; value: string }>,
  ) => {
    setStaticHeadersArray(headers);
    if (headers.length === 0) {
      onChange("staticAuthHeaders", "");
    } else {
      const obj = headers.reduce(
        (acc, { name, value }) => {
          if (name.trim()) acc[name] = value;
          return acc;
        },
        {} as Record<string, string>,
      );
      onChange("staticAuthHeaders", JSON.stringify(obj));
    }
  };

  const handleDynamicHeadersChange = (
    headers: Array<{ name: string; value: string }>,
  ) => {
    setDynamicHeadersArray(headers);
    if (headers.length === 0) {
      onChange("dynamicAuthHeaders", "");
    } else {
      const obj = headers.reduce(
        (acc, { name, value }) => {
          if (name.trim()) acc[name] = value;
          return acc;
        },
        {} as Record<string, string>,
      );
      onChange("dynamicAuthHeaders", JSON.stringify(obj));
    }
  };

  // Validation: For CustomToken and OAuth, at least one header (static OR dynamic) is required
  const isHeadersRequired =
    authorizationType === "CustomToken" || authorizationType === "OAuth";
  const hasNoHeaders =
    staticHeadersArray.length === 0 && dynamicHeadersArray.length === 0;
  const hasHeadersError = isHeadersRequired && hasNoHeaders;

  return (
    <Flex direction="column" gap="4">
      <Field label={t("app.servers.authorization.authorizationType")}>
        <Select
          items={[
            {
              label: t("app.servers.authorization.noneNoAuthRequired"),
              value: "None",
            },
            {
              label: t("app.servers.authorization.customTokenApiKey"),
              value: "CustomToken",
            },
            {
              label: t("app.servers.authorization.basicAuthUsernamePassword"),
              value: "BasicAuth",
            },
            {
              label: t("app.servers.authorization.oauthDynamicToken"),
              value: "OAuth",
            },
            {
              label: t("app.servers.authorization.customAuthEndpointAutomatic"),
              value: "CustomAuthEndpoint",
            },
          ]}
          value={[authorizationType]}
          onValueChange={(details) =>
            onChange("authorizationType", details.value[0])
          }
          disabled={disabled}
        />
        <Text fontSize="sm" color="gray.600" mt={1}>
          {authorizationType === "None" &&
            t("app.servers.authorization.noAuthorizationRequired")}
          {authorizationType === "CustomToken" &&
            t("app.servers.authorization.usersProvideApiKeys")}
          {authorizationType === "BasicAuth" &&
            t("app.servers.authorization.usersProvideUsernamePassword")}
          {authorizationType === "OAuth" &&
            t("app.servers.authorization.usersOAuthFlow")}
          {authorizationType === "CustomAuthEndpoint" &&
            t("app.servers.authorization.backendAutoFetchTokens")}
        </Text>
      </Field>

      {authorizationType === "CustomAuthEndpoint" && (
        <>
          {/* Static Authorization Headers for CustomAuthEndpoint */}
          <Box>
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontSize="sm" fontWeight="medium">
                {t("app.servers.authorization.staticHeadersOptional")}
              </Text>
              <Button
                size="xs"
                onClick={() =>
                  handleStaticHeadersChange([
                    ...staticHeadersArray,
                    { name: "", value: "" },
                  ])
                }
                colorPalette="blue"
                variant="outline"
                disabled={disabled}
              >
                <MdAdd />
                {t("app.servers.authorization.addHeader")}
              </Button>
            </Flex>
            <Text fontSize="xs" color="gray.500" mb={2}>
              {t("app.servers.authorization.headersAppliedToAllRequests")}
            </Text>
            <VStack gap={2}>
              {staticHeadersArray.map((header, index) => (
                <HStack key={index} gap={2} w="full">
                  <Input
                    placeholder={t("app.servers.authorization.headerName")}
                    value={header.name}
                    onChange={(e) => {
                      const updated = [...staticHeadersArray];
                      updated[index].name = e.target.value;
                      handleStaticHeadersChange(updated);
                    }}
                    flex={1}
                    size="sm"
                    disabled={disabled}
                  />
                  <Input
                    placeholder={t("app.servers.authorization.headerValue")}
                    value={header.value}
                    onChange={(e) => {
                      const updated = [...staticHeadersArray];
                      updated[index].value = e.target.value;
                      handleStaticHeadersChange(updated);
                    }}
                    flex={1}
                    size="sm"
                    disabled={disabled}
                  />
                  <Button
                    size="sm"
                    colorPalette="red"
                    variant="ghost"
                    onClick={() =>
                      handleStaticHeadersChange(
                        staticHeadersArray.filter((_, i) => i !== index),
                      )
                    }
                    disabled={disabled}
                  >
                    <MdDelete />
                  </Button>
                </HStack>
              ))}
              {staticHeadersArray.length === 0 && (
                <Text fontSize="xs" color="gray.500">
                  {t("app.servers.authorization.noStaticHeadersConfigured")}
                </Text>
              )}
            </VStack>
          </Box>

          <TokenEndpointConfig
            autoTokenEndpoint={autoTokenEndpoint}
            autoTokenMethod={autoTokenMethod}
            autoTokenRequestHeaders={autoTokenRequestHeaders}
            autoTokenRequestBody={autoTokenRequestBody}
            autoTokenRequestPlaceholders={autoTokenRequestPlaceholders}
            autoTokenResponseExample={autoTokenResponseExample}
            autoTokenResponsePath={autoTokenResponsePath}
            autoTokenHeaderName={autoTokenHeaderName}
            autoTokenHeaderFormat={autoTokenHeaderFormat}
            autoTokenSecret={autoTokenSecret}
            refreshTokenPath={refreshTokenPath}
            refreshTokenEndpoint={refreshTokenEndpoint}
            refreshTokenMethod={refreshTokenMethod}
            refreshTokenRequestHeaders={refreshTokenRequestHeaders}
            refreshTokenRequestBody={refreshTokenRequestBody}
            refreshTokenResponsePath={refreshTokenResponsePath}
            onChange={onChange}
            disabled={disabled}
            errors={errors}
          />
        </>
      )}

      {authorizationType !== "None" &&
        authorizationType !== "CustomAuthEndpoint" && (
          <>
            {/* Static Authorization Headers */}
            <Box>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="sm" fontWeight="medium">
                  {t("app.servers.authorization.staticHeadersOptional")}
                </Text>
                <Button
                  size="xs"
                  onClick={() =>
                    handleStaticHeadersChange([
                      ...staticHeadersArray,
                      { name: "", value: "" },
                    ])
                  }
                  colorPalette="blue"
                  variant="outline"
                  disabled={disabled}
                >
                  <MdAdd />
                  {t("app.servers.authorization.addHeader")}
                </Button>
              </Flex>
              <Text fontSize="xs" color="gray.500" mb={2}>
                {t("app.servers.authorization.headersAppliedToAllRequests")}
              </Text>
              <VStack gap={2}>
                {staticHeadersArray.map((header, index) => (
                  <HStack key={index} gap={2} w="full">
                    <Input
                      placeholder={t("app.servers.authorization.headerName")}
                      value={header.name}
                      onChange={(e) => {
                        const updated = [...staticHeadersArray];
                        updated[index].name = e.target.value;
                        handleStaticHeadersChange(updated);
                      }}
                      flex={1}
                      size="sm"
                      disabled={disabled}
                    />
                    <Input
                      placeholder={t("app.servers.authorization.headerValue")}
                      value={header.value}
                      onChange={(e) => {
                        const updated = [...staticHeadersArray];
                        updated[index].value = e.target.value;
                        handleStaticHeadersChange(updated);
                      }}
                      flex={1}
                      size="sm"
                      disabled={disabled}
                    />
                    <Button
                      size="sm"
                      colorPalette="red"
                      variant="ghost"
                      onClick={() =>
                        handleStaticHeadersChange(
                          staticHeadersArray.filter((_, i) => i !== index),
                        )
                      }
                      disabled={disabled}
                    >
                      <MdDelete />
                    </Button>
                  </HStack>
                ))}
                {staticHeadersArray.length === 0 && (
                  <Text fontSize="xs" color="gray.500">
                    {t("app.servers.authorization.noStaticHeadersConfigured")}
                  </Text>
                )}
              </VStack>
            </Box>

            {/* Dynamic Authorization Headers */}
            {authorizationType !== "BasicAuth" && (
              <Box>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    {t(
                      "app.servers.authorization.dynamicHeadersSchemaOptional",
                    )}
                  </Text>
                  <Button
                    size="xs"
                    onClick={() =>
                      handleDynamicHeadersChange([
                        ...dynamicHeadersArray,
                        { name: "", value: "" },
                      ])
                    }
                    colorPalette="blue"
                    variant="outline"
                    disabled={disabled}
                  >
                    <MdAdd />
                    {t("app.servers.authorization.addHeader")}
                  </Button>
                </Flex>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  {t("app.servers.authorization.headersUsersMustProvide")}
                </Text>
                <VStack gap={2}>
                  {dynamicHeadersArray.map((header, index) => (
                    <HStack key={index} gap={2} w="full">
                      <Input
                        placeholder={t(
                          "app.servers.authorization.headerNameAuthorizationExample",
                        )}
                        value={header.name}
                        onChange={(e) => {
                          const updated = [...dynamicHeadersArray];
                          updated[index].name = e.target.value;
                          handleDynamicHeadersChange(updated);
                        }}
                        flex={1}
                        size="sm"
                        disabled={disabled}
                      />
                      <Input
                        placeholder={
                          authorizationType === "CustomToken"
                            ? t("app.servers.authorization.bearerApiKey")
                            : t("app.servers.authorization.bearerAccessToken")
                        }
                        value={header.value}
                        onChange={(e) => {
                          const updated = [...dynamicHeadersArray];
                          updated[index].value = e.target.value;
                          handleDynamicHeadersChange(updated);
                        }}
                        flex={1}
                        size="sm"
                        disabled={disabled}
                      />
                      <Button
                        size="sm"
                        colorPalette="red"
                        variant="ghost"
                        onClick={() =>
                          handleDynamicHeadersChange(
                            dynamicHeadersArray.filter((_, i) => i !== index),
                          )
                        }
                        disabled={disabled}
                      >
                        <MdDelete />
                      </Button>
                    </HStack>
                  ))}
                  {dynamicHeadersArray.length === 0 && (
                    <Text fontSize="xs" color="gray.500">
                      {t(
                        "app.servers.authorization.noDynamicHeadersConfigured",
                      )}
                    </Text>
                  )}
                </VStack>
              </Box>
            )}

            {/* Error message when no headers are configured for CustomToken/OAuth */}
            {hasHeadersError && (
              <Box
                p={3}
                borderRadius="md"
                bg={{ base: "red.50", _dark: "red.900" }}
                borderWidth="1px"
                borderColor={{ base: "red.500", _dark: "red.400" }}
              >
                <Text fontSize="sm" color="red.600" fontWeight="medium">
                  {t("app.servers.authorization.atLeastOneHeaderRequired")}
                </Text>
                <Text fontSize="xs" color="red.600" mt={1}>
                  {t("app.servers.authorization.configureAtLeastOneHeader")}
                </Text>
              </Box>
            )}

            <Field
              label={t("app.servers.authorization.authInstructionsUrlOptional")}
              helperText={t(
                "app.servers.authorization.authInstructionsUrlHelp",
              )}
            >
              <Input
                value={authInstructionsUrl || ""}
                onChange={(e) =>
                  onChange("authInstructionsUrl", e.target.value)
                }
                placeholder={t(
                  "app.servers.authorization.authInstructionsUrlPlaceholder",
                )}
                disabled={disabled}
              />
            </Field>
          </>
        )}
    </Flex>
  );
};
